const DEFAULT_API_ROOT = "/api/v1";

export function resolveApiRoot(configuredUrl?: string) {
  const value = configuredUrl?.trim();
  return (value || DEFAULT_API_ROOT).replace(/\/+$/, "");
}

const API_ROOT = resolveApiRoot(import.meta.env.VITE_API_URL);
export const SESSION_EXPIRED_EVENT = "voyage:session-expired";

const NO_REFRESH_PATHS = new Set(["/auth/login", "/auth/register", "/auth/refresh"]);
let refreshInFlight: Promise<void> | null = null;

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

function readCookie(name: string) {
  return document.cookie.split("; ").find((part) => part.startsWith(`${name}=`))?.split("=").slice(1).join("=");
}

function csrfToken() {
  return readCookie("voyage_csrf") || sessionStorage.getItem("voyage_csrf");
}

function isUnsafe(method?: string) {
  return Boolean(method && !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase()));
}

async function request(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("content-type", "application/json");
  const csrf = csrfToken();
  if (csrf && init.method && !["GET", "HEAD"].includes(init.method.toUpperCase())) headers.set("x-csrf-token", decodeURIComponent(csrf));
  return fetch(`${API_ROOT}${path}`, { ...init, headers, credentials: "include" });
}

async function apiError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => ({ detail: "Something went wrong" }));
  const detail = Array.isArray(body.detail) ? body.detail.map((item: { msg: string }) => item.msg).join(", ") : body.detail;
  return new ApiError(response.status, detail || "Something went wrong");
}

async function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const response = await fetch(`${API_ROOT}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw await apiError(response);
      const result = await response.json() as { csrf_token: string };
      sessionStorage.setItem("voyage_csrf", result.csrf_token);
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function expireSession() {
  sessionStorage.removeItem("voyage_csrf");
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  // With a direct cross-origin API URL, browser JavaScript cannot read the
  // API host's CSRF cookie after a reload. Rotate the refresh session once to
  // recover the token before sending the first state-changing request.
  if (isUnsafe(init.method) && !NO_REFRESH_PATHS.has(path) && !csrfToken()) {
    await refreshSession().catch(() => undefined);
  }

  let response = await request(path, init);

  if (response.status === 401 && !NO_REFRESH_PATHS.has(path)) {
    // A failed refresh may be a cross-tab rotation race. Retrying the original
    // request once lets a session renewed by another tab recover cleanly.
    await refreshSession().catch(() => undefined);
    response = await request(path, init);
    if (response.status === 401) expireSession();
  }

  if (!response.ok) {
    throw await apiError(response);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function money(value: string | number | null | undefined, currency = "INR") {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value || 0));
}

export function dayMonth(value: string | null) {
  if (!value) return "Dates to be decided";
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(new Date(`${value}T00:00:00`));
}

export function daysUntil(value: string | null) {
  if (!value) return null;
  return Math.max(0, Math.ceil((new Date(`${value}T00:00:00`).getTime() - Date.now()) / 86_400_000));
}
